import { createMemo, createUniqueId, For, Index, Match, Show, Switch, VoidComponent } from "solid-js";
import { useTranslation } from "../../state/i18n";
import InfoButton from "../infoButton/InfoButton";
import { IconButton, TextAndIconButton } from "../button/Button";
import type { EmptyAction, RuleAction } from "../../state/formVersion";
import { RuleActionKey } from "../../state/formVersion";
import FieldId from "../../fields/FieldId";
import { CompactTextInput } from "../inputs/TextInput";
import {
  ACTION_FIELD_VALUE_TYPES,
  MAX_RULE_ACTIONS,
  VALUE_ACTION_KEYS,
  VALUE_FIELD_REFERENCE_KEYS,
  VISIBILITY_ACTION_KEYS,
} from "../../constants";
import { isEmpty, isNil } from "lodash-es";
import { fields } from "../../fields/fields";
import InlineMessage from "../inlineMessage/InlineMessage";
import { toPlaceholderFieldOption } from "../../utils/selectOptionsUtil";
import type { Issue } from "../../state/validation";
import { getPredefinedOptions, getScopedFieldOptions, getVariableOptions } from "../../utils/ruleUtil";
import { getErrorMessagesForKey } from "../../utils/validationParserUtil";
import { CompactDateInput, CompactDateTimeInput, CompactTimeInput } from "../inputs/DateInput";
import type { FieldConfig, RuleActionBehaviour, RuleActionVariant, VariableConfig } from "../../fields/types";
import SingleSelectWithSearch from "../menu/SingleSelectWithSearch";
import type { DynamicValueId, DynamicValueOption, SelectOption } from "../../types/Menu";
import { SingleSelect } from "../menu/Select";
import { narrow } from "../../utils/solidUtils.ts";
import VariableId from "../../variables/VariableId.ts";

type RuleActionsProps = {
  fields: FieldConfig[];
  variables: VariableConfig[];
  value: RuleAction[];
  onChange: (actions: Partial<RuleAction>[], changedAction: Partial<RuleAction>) => void;
  getField: <T = Record<string, unknown>>(fieldId: FieldId) => FieldConfig<T> | undefined;
  issues: Issue[];
};

const RuleActions: VoidComponent<RuleActionsProps> = (props) => {
  const { t } = useTranslation();

  const keyOptions = createMemo<SelectOption<RuleActionKey>[]>(() => [
    { variant: "text", label: t(($) => $.RULES_ACTIONS_VISIBILITY_HIDDEN), value: RuleActionKey.HIDDEN },
    { variant: "text", label: t(($) => $.RULES_ACTIONS_VISIBILITY_VISIBLE), value: RuleActionKey.VISIBLE },
    { variant: "text", label: t(($) => $.RULES_ACTIONS_SET_VALUE), value: RuleActionKey.VALUE },
    { variant: "text", label: t(($) => $.RULES_ACTIONS_SET_REFERENCE_VALUE), value: RuleActionKey.REFERENCE },
  ]);

  const onChange = (action: Partial<RuleAction>, index: number) => {
    props.onChange([...props.value.slice(0, index), action, ...props.value.slice(index + 1)], action);
  };

  const onAdd = (action: EmptyAction) => {
    props.onChange([...props.value, action], action);
  };

  const onDelete = (index: number) => {
    const deletedAction = props.value[index];
    props.onChange(
      props.value.filter((_, i) => i !== index),
      deletedAction,
    );
  };

  return (
    <section aria-labelledby="actions-title" class="flex flex-col gap-4">
      <div class="flex items-center">
        <h3 id="actions-title" class="text-header-lg text-primary">
          {t(($) => $.RULES_ACTIONS_TITLE)}
        </h3>
        <InfoButton
          title={t(($) => $.RULES_ACTIONS_TITLE)}
          classList={{ "ml-auto": true }}
          description={t(($) => $.RULES_ACTIONS_DESCRIPTION)}
          ariaLabel={t(($) => $.RULES_ACTIONS_TITLE)}
        />
      </div>
      <div class="flex flex-col gap-2">
        <Index each={props.value} fallback={<EmptyActionComponent onAdd={onAdd} fields={props.fields} index={0} />}>
          {(action, index) => {
            const errors = createMemo(() =>
              getErrorMessagesForKey("actions", props.issues)?.filter((issue) => issue.index === index),
            );
            const actionConfig = createMemo(() =>
              getActionConfig(props.fields, props.variables, props.getField, props.value, action()),
            );
            return (
              <div class="flex grow flex-row items-center gap-2">
                <div class="flex w-full gap-2">
                  <div class="max-w-44 min-w-16 basis-44">
                    <SingleSelectWithSearch
                      id={createUniqueId()}
                      ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_ARIA)}
                      placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_PLACEHOLDER)}
                      onSelect={(value) => {
                        if (!isNil(value) && value instanceof FieldId) {
                          onChange({ fieldId: value }, index);
                        }
                      }}
                      value={action().fieldId}
                      options={{
                        Fields: actionConfig()?.fields,
                      }}
                      errorMessage={errors().find((x) => x.property === "fieldId")?.message}
                      width="480px"
                    />
                  </div>
                  <div class="max-w-44 min-w-16 basis-44">
                    <Show when={!isNil(action().fieldId)}>
                      <Show when={actionConfig()?.keys}>
                        {(options) => (
                          <SingleSelect
                            ariaLabel={t(($) => $.RULES_ACTIONS_ACTION_ARIA)}
                            placeholder={t(($) => $.RULES_ACTIONS_ACTION_PLACEHOLDER)}
                            onSelect={(key) => {
                              if (action().key === key) {
                                return;
                              }
                              if (key === RuleActionKey.REFERENCE) {
                                const unwrappedAction = action();
                                const value =
                                  unwrappedAction.key === RuleActionKey.REFERENCE ? unwrappedAction.value : undefined;
                                onChange({ fieldId: action().fieldId, key, value }, index);
                              } else if (key === RuleActionKey.VALUE) {
                                const value = action().key !== RuleActionKey.VALUE ? "" : undefined;
                                onChange({ fieldId: action().fieldId, key, value }, index);
                              } else {
                                onChange({ fieldId: action().fieldId, key }, index);
                              }
                            }}
                            value={action().key}
                            options={keyOptions().filter((key) => options().includes(key.value))}
                            hasError={!isNil(errors().find((x) => x.property === "key")?.message)}
                            sameWidth={false}
                          />
                        )}
                      </Show>
                    </Show>
                  </div>
                  <div class="grow">
                    <Show when={narrow(action(), (a) => a.key === RuleActionKey.VALUE)}>
                      {(valueAction) => (
                        <Switch>
                          <Match when={actionConfig()?.variant === "select"}>
                            <SingleSelect
                              ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_ARIA)}
                              placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_SELECT_PLACEHOLDER)}
                              onSelect={(value) => {
                                onChange({ ...valueAction(), value }, index);
                              }}
                              options={(actionConfig() as SelectActionConfig).options ?? []}
                              value={valueAction().value}
                              hasError={!isNil(errors().find((x) => x.property === "value")?.message)}
                              sameWidth={false}
                            />
                          </Match>
                          <Match when={actionConfig()?.variant === "boolean"}>
                            <SingleSelect
                              ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_ARIA)}
                              placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_SELECT_PLACEHOLDER)}
                              onSelect={(value) => onChange({ ...valueAction(), value }, index)}
                              options={[
                                { variant: "text", label: t(($) => $.RULES_CONDITIONS_CHECKED), value: true },
                                { variant: "text", label: t(($) => $.RULES_CONDITIONS_UNCHECKED), value: false },
                              ]}
                              value={!isNil(valueAction().value) ? Boolean(valueAction().value).valueOf() : undefined}
                              hasError={!isNil(errors().find((x) => x.property === "value")?.message)}
                              sameWidth={false}
                            />
                          </Match>
                          <Match when={actionConfig()?.variant === "date"}>
                            <CompactDateInput
                              id={`action-${index}`}
                              ariaLabel={t(($) => $.RULES_ACTIONS_ACTION_DATE_ARIA)}
                              value={valueAction().value?.toString() ?? ""}
                              onBlur={(e) => {
                                onChange({ ...valueAction(), value: e.target.value }, index);
                              }}
                              errorMessage={errors().find((x) => x.property === "value")?.message}
                            />
                          </Match>
                          <Match when={actionConfig()?.variant === "dateTime"}>
                            <CompactDateTimeInput
                              id={`action-${index}`}
                              ariaLabel={t(($) => $.RULES_ACTIONS_ACTION_DATETIME_ARIA)}
                              value={valueAction().value?.toString() ?? ""}
                              onBlur={(e) => {
                                onChange({ ...valueAction(), value: e.target.value }, index);
                              }}
                              errorMessage={errors().find((x) => x.property === "value")?.message}
                            />
                          </Match>
                          <Match when={actionConfig()?.variant === "time"}>
                            <CompactTimeInput
                              id={`action-${index}`}
                              ariaLabel={t(($) => $.RULES_ACTIONS_ACTION_TIME_ARIA)}
                              value={valueAction().value?.toString() ?? ""}
                              onBlur={(e) => {
                                onChange({ ...valueAction(), value: e.target.value }, index);
                              }}
                              errorMessage={errors().find((x) => x.property === "value")?.message}
                            />
                          </Match>
                          <Match when={actionConfig()?.variant === "text"}>
                            <CompactTextInput
                              id={`action-${index}`}
                              ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_ARIA)}
                              value={valueAction().value?.toString() ?? ""}
                              placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_PLACEHOLDER)}
                              onBlur={(e) => {
                                onChange({ ...valueAction(), value: e.target.value.trim() }, index);
                              }}
                              errorMessage={errors().find((x) => x.property === "value")?.message}
                            />
                          </Match>
                        </Switch>
                      )}
                    </Show>
                    <Show when={narrow(action(), (a) => a.key === RuleActionKey.REFERENCE)}>
                      {(referenceValueAction) => (
                        <SingleSelectWithSearch
                          id={createUniqueId()}
                          ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_ARIA)}
                          placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_SELECT_PLACEHOLDER)}
                          onSelect={(id) => {
                            onChange({ ...referenceValueAction(), value: id ?? undefined }, index);
                          }}
                          value={referenceValueAction().value}
                          options={{
                            Fields: (actionConfig() as FieldActionConfig).options.fields,
                            Variables: (actionConfig() as FieldActionConfig).options.variables,
                          }}
                          errorMessage={errors().find((x) => x.property === "value")?.message}
                          width="480px"
                        />
                      )}
                    </Show>
                  </div>
                </div>
                <div class="ml-auto shrink-0">
                  <IconButton
                    variant="tertiary"
                    icon="trash"
                    size="medium"
                    aria-label={t(($) => $.DELETE_ARIA)}
                    onClick={() => {
                      onDelete(index);
                    }}
                  />
                </div>
              </div>
            );
          }}
        </Index>
      </div>
      <div class="flex flex-col gap-4">
        <Show
          when={getErrorMessagesForKey(
            "actions",
            props.issues.filter((issue) => issue.scope === "parent"),
          )}
        >
          {(issues) => (
            <Switch>
              <Match when={issues().length === 1}>
                <InlineMessage variant="danger" label={issues()[0].message} />
              </Match>
              <Match when={issues().length > 1}>
                <InlineMessage variant="danger">
                  <span>{t(($) => $.VALIDATION_RULES_ACTION_INLINE_MESSAGE_DESCRIPTION)}</span>
                  <ul class="list-none">
                    <For each={issues()}>
                      {(issue) => (
                        <li class="flex">
                          <span class="mr-1.5 ml-2">•</span>
                          {issue.message}
                        </li>
                      )}
                    </For>
                  </ul>
                </InlineMessage>
              </Match>
            </Switch>
          )}
        </Show>
        <Switch>
          <Match when={props.value?.length <= MAX_RULE_ACTIONS}>
            <div>
              <TextAndIconButton
                disabled={isEmpty(props.value)}
                text={t(($) => $.RULES_ACTIONS_ADD_BUTTON)}
                variant="light"
                size="medium"
                icon="plus"
                onClick={() => {
                  onAdd({ key: RuleActionKey.EMPTY });
                }}
              />
            </div>
          </Match>
          <Match when={props.value?.length > MAX_RULE_ACTIONS}>
            <InlineMessage
              variant="warning"
              label={t(($) => $.RULES_ACTIONS_LIMIT_INLINE, { count: MAX_RULE_ACTIONS })}
            />
          </Match>
        </Switch>
      </div>
    </section>
  );
};

type EmptyActionComponentProps = {
  fields: FieldConfig[];
  onAdd: (value: EmptyAction) => void;
  index: number;
};
const EmptyActionComponent: VoidComponent<EmptyActionComponentProps> = (props) => {
  const { t } = useTranslation();
  return (
    <div class="flex grow flex-row items-center gap-2">
      <div class="grid w-full grid-cols-4 gap-2">
        <SingleSelectWithSearch
          id={createUniqueId()}
          ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_ARIA)}
          placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_PLACEHOLDER)}
          onSelect={(value) => {
            if (value instanceof FieldId) {
              props.onAdd({ fieldId: value, key: RuleActionKey.EMPTY });
            }
          }}
          options={{ Fields: props.fields.flatMap(toPlaceholderFieldOption) }}
          errorMessage={t(($) => $.VALIDATION_RULES_ACTIONS_REQUIRED)}
          width="480px"
        />
      </div>
    </div>
  );
};

type BaseActionConfig = {
  variant: RuleActionVariant;
  keys: RuleActionKey[];
  fields: DynamicValueOption<DynamicValueId>[];
};

export type FieldActionConfig = BaseActionConfig & {
  variant: "date" | "time" | "dateTime";
  options: {
    fields: DynamicValueOption<FieldId>[];
    variables: DynamicValueOption<VariableId>[];
  };
};

export type SelectActionConfig = BaseActionConfig & {
  variant: "select";
  options: SelectOption<string>[];
};

export type ActionConfig = BaseActionConfig | FieldActionConfig | SelectActionConfig;

/**
 * Setting values is not supported for every single field. Check whether this one is.
 */
const isSetValueAllowed = (actionBehaviour?: RuleActionBehaviour) =>
  !isEmpty(actionBehaviour?.setValue?.fieldTypes) ||
  actionBehaviour?.variant === "select" ||
  actionBehaviour?.variant === "boolean";

/**
 * Conditionally hide keys when they're already set in other actions.
 */
const getScopedKeys = (
  field: FieldConfig,
  actions: Partial<RuleAction>[],
  action: Partial<RuleAction>,
  actionBehaviour?: RuleActionBehaviour,
) => {
  const setValueAllowed = isSetValueAllowed(actionBehaviour);
  const hasVisibilityKey = actions.some(
    (value) =>
      value.fieldId?.equals(action.fieldId) === true &&
      (value.key === RuleActionKey.VISIBLE || value.key === RuleActionKey.HIDDEN),
  );
  const hasSetValueKey = actions.some(
    (value) =>
      value.fieldId?.equals(action.fieldId) === true &&
      (value.key === RuleActionKey.VALUE || value.key === RuleActionKey.REFERENCE),
  );

  const actionKeys = ACTION_FIELD_VALUE_TYPES.includes(field.type) ? VALUE_FIELD_REFERENCE_KEYS : VALUE_ACTION_KEYS;
  return [
    ...((setValueAllowed && !hasSetValueKey) || actionKeys.includes(action.key ?? "empty") ? actionKeys : []),
    ...(!hasVisibilityKey || VISIBILITY_ACTION_KEYS.includes(action.key ?? "empty") ? VISIBILITY_ACTION_KEYS : []),
  ];
};

const getActionConfig = (
  formFields: FieldConfig[],
  variables: VariableConfig[],
  getField: <T = Record<string, unknown>>(fieldId: FieldId) => FieldConfig<T> | undefined,
  actions: Partial<RuleAction>[],
  action: Partial<RuleAction>,
): ActionConfig => {
  const field = !isNil(action.fieldId) ? getField(action.fieldId) : undefined;

  const scopedFields = formFields.filter((formField) => {
    if (!isNil(action.fieldId) && formField.uid.equals(action.fieldId)) {
      return true;
    }
    const fieldActions = actions.filter((action) => formField.uid.equals(action.fieldId));
    return isSetValueAllowed(fields[formField.type].ruleBehaviour?.actions)
      ? fieldActions.length < 2
      : isEmpty(fieldActions);
  });
  const scopedOptions = scopedFields.flatMap(toPlaceholderFieldOption);

  if (isNil(field)) {
    return { variant: "none", keys: [], fields: scopedOptions };
  }

  const actionBehaviour = fields[field.type].ruleBehaviour?.actions;

  if (!isNil(action.fieldId) && !isNil(actionBehaviour)) {
    if (actionBehaviour.variant === "select") {
      return {
        variant: actionBehaviour.variant,
        fields: scopedOptions,
        keys: getScopedKeys(field, actions, action, actionBehaviour),
        options: getPredefinedOptions(field),
      };
    }
    if (ACTION_FIELD_VALUE_TYPES.includes(actionBehaviour.variant)) {
      return {
        variant: actionBehaviour.variant,
        fields: scopedOptions,
        keys: getScopedKeys(field, actions, action, actionBehaviour),
        options: {
          fields: getScopedFieldOptions(field.uid, scopedFields, actionBehaviour.setValue?.fieldTypes ?? []),
          variables: getVariableOptions(variables, actionBehaviour.setValue?.variableTypes ?? []),
        },
      };
    }
  }
  return {
    variant: actionBehaviour?.variant ?? "none",
    fields: scopedOptions,
    keys: getScopedKeys(field, actions, action, actionBehaviour),
  };
};

export default RuleActions;
